/**
 * NotificationContext
 * --------------------
 * - Polls /api/tasks/notifications every 25 s to refresh the bell + dot.
 * - Plays a short tone + fires a browser Notification when the unread
 *   count increases.
 * - Exposes: { unread, pendingCount, hasNew, notifications, markAllRead, refresh, requestPushPermission }.
 *
 * Designed to live INSIDE <AuthProvider> so it can rely on `useAuth()`.
 */
import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { log } from '@/lib/log';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 10_000;

const NotificationContext = createContext(null);

// Tiny "ping" sound via WebAudio — no external file needed.
const playPing = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.4);
    setTimeout(() => ctx.close(), 600);
  } catch (err) {
    log.debug('[notifications] audio ping blocked:', err?.message || err);
  }
};

const showBrowserPush = (message) => {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;
  try {
    new Notification('RA License — New task', {
      body: message,
      icon: '/logo.png',
      tag: 'ral-task',          // collapses repeats
      renotify: true,
    });
  } catch (err) {
    log.debug('[notifications] browser push failed:', err?.message || err);
  }
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [hasNew, setHasNew] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loaded, setLoaded] = useState(false);  // true after the first successful poll
  const prevUnreadRef = useRef(0);
  const initialisedRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const r = await axios.get(`${API}/tasks/notifications`);
      const next = r.data;
      const nextUnread = next.unread || 0;
      // Trigger sound + push if unread went UP (skip on first load)
      if (initialisedRef.current && nextUnread > prevUnreadRef.current) {
        playPing();
        const latest = (next.notifications || []).find((n) => !n.read);
        if (latest) showBrowserPush(latest.message);
      }
      prevUnreadRef.current = nextUnread;
      initialisedRef.current = true;
      setUnread(nextUnread);
      setPendingCount(next.pending_count || 0);
      setHasNew(!!next.has_new);
      setNotifications(next.notifications || []);
      setLoaded(true);
    } catch (err) {
      // 401 will be handled by the global axios interceptor in AuthContext
      log.warn('[notifications] poll failed:', err?.message || err);
    }
  }, [user]);

  const markAllRead = useCallback(async () => {
    try {
      await axios.post(`${API}/tasks/notifications/mark-read`);
      setUnread(0);
      setNotifications((list) => list.map((n) => ({ ...n, read: true })));
      prevUnreadRef.current = 0;
    } catch (err) {
      log.warn('[notifications] mark-read failed:', err?.message || err);
    }
  }, []);

  const requestPushPermission = useCallback(async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
    if (Notification.permission === 'granted') return 'granted';
    if (Notification.permission === 'denied') return 'denied';
    try {
      return await Notification.requestPermission();
    } catch {
      return 'denied';
    }
  }, []);

  // Poll lifecycle — only while a user is logged in
  useEffect(() => {
    if (!user) {
      setUnread(0); setPendingCount(0); setHasNew(false); setNotifications([]);
      prevUnreadRef.current = 0; initialisedRef.current = false;
      return undefined;
    }
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [user, refresh]);

  const value = { unread, pendingCount, hasNew, notifications, loaded, markAllRead, refresh, requestPushPermission };
  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used inside <NotificationProvider>');
  return ctx;
};
